import { Stack } from '@mui/material';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Paths } from 'services/navigation/paths';
import { Header } from 'common/ui/Header';
import { CampaignType } from 'generated-graphql/types';
import { RadioGroup } from 'common/form';
import { useFormik } from 'formik';
import Yup from 'utils/validation';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { CampaignCreationCancelButton } from 'modules/campaign-creation/components/CampaignCreationCancelButton';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { useSelectedCandidateBatchStatusesHandler } from 'modules/campaign-creation/hooks/useSelectedCandidateBatchStatusesHandler';
import { TrackingEventName } from 'services/tracking/constants';
import { useTracking } from 'services/tracking/hooks';

export const CampaignCreationCandidateApplicationStep: React.FC = () => {
  const state = useCampaignCreationStore((store) => store.state);
  const campaignId = useCampaignCreationStore((store) => store.campaignId);
  const campaignType = useCampaignCreationStore(
    (store) => store.state?.campaignType
  );
  const onCandidateApplicationSubmit = useCampaignCreationStore(
    (store) => store.onCandidateApplicationSubmit
  );
  const onCandidateApplicationBack = useCampaignCreationStore(
    (store) => store.onCandidateApplicationBack
  );
  const { track } = useTracking();
  const { candidateBatchStatuses } = useSelectedCandidateBatchStatusesHandler();

  const formik = useFormik({
    initialValues: {
      campaignType: campaignType
    },
    validationSchema: Yup.object({
      campaignType: Yup.mixed<CampaignType>()
        .oneOf([
          CampaignType.ApplicantOutreach,
          CampaignType.NewCandidateOutreach
        ])
        .required('Choose an option.')
    }),
    onSubmit: (values) => {
      track(
        TrackingEventName.CampaignCreationChannelAlreadyAppliedOptionsChange,
        {
          campaignType: values.campaignType,
          alreadyApplied: values.campaignType === CampaignType.ApplicantOutreach
        }
      );
      onCandidateApplicationSubmit({
        campaignType: values.campaignType as CampaignType
      });
    }
  });

  return (
    <>
      <Header
        title="Choose recipients"
        breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
        currentBreadcrumb={getTitle(state)}
        onBack={() =>
          onCandidateApplicationBack({
            selectedCandidateBatchStatuses: candidateBatchStatuses
          })
        }
      />
      <Stack spacing="40px">
        <RadioGroup
          field={{
            label:
              'When contacted, will the candidates have applied to the job?',
            options: [
              {
                value: CampaignType.ApplicantOutreach,
                label: 'Yes'
              },
              {
                value: CampaignType.NewCandidateOutreach,
                label: 'No'
              }
            ]
          }}
          error={!!formik.errors.campaignType}
          errorMessage={formik.errors.campaignType}
          value={formik.values.campaignType}
          onChange={(e) => {
            formik.setFieldValue('campaignType', e as CampaignType);
          }}
        />
        <Stack direction="row" gap="15px">
          <CampaignCreationNextButton
            handleNext={() => formik.handleSubmit()}
          />
          <CampaignCreationCancelButton campaignId={campaignId} />
        </Stack>
      </Stack>
    </>
  );
};
